import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../hooks/useAuthStore';
import { Search, Filter, ChevronLeft, ChevronRight, Eye, Download, RefreshCw, Loader2 } from 'lucide-react';
import clsx from 'clsx';

interface Candidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  whatsapp_name: string | null;
  whatsapp_phone: string;
  current_phase: number;
  phase_status: string;
  status: string;
  assigned_minister_id: string | null;
  last_interaction: string;
  created_at: string;
}

const PHASE_NAMES = ['Onboarding', 'Valoración', 'Rastreo', 'Ejecución', 'Seguimiento'];
const STATUS_LABELS: Record<string, string> = { active: 'Activo', paused: 'Pausado', completed: 'Completado', abandoned: 'Abandonado' };
export function CandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');

  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';
  const phase = searchParams.get('phase') || '';
  const limit = 10;

  const fetchCandidates = async () => {
    setIsLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.append('status', status);
    if (phase) params.append('phase', phase);
    if (searchTerm) params.append('search', searchTerm);

    const result = await apiRequest<Candidate[]>(`/candidates?${params}`);
    if (result.success && result.data) {
      setCandidates(result.data);
      setTotal(result.meta?.total || result.data.length);
    }
    setIsLoading(false);
  };

  useEffect(() => { fetchCandidates(); }, [page, status, phase]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    params.set('search', searchTerm);
    params.set('page', '1');
    setSearchParams(params);
    fetchCandidates();
  };

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  };

  const totalPages = Math.ceil(total / limit);

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = { active: 'badge-primary', paused: 'badge-warning', completed: 'badge-success', abandoned: 'badge-danger' };
    return <span className={clsx('badge', classes[status] || 'badge-gray')}>{STATUS_LABELS[status] || status}</span>;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all candidates (up to 1000) with current filters
      const params = new URLSearchParams({ page: '1', limit: '1000' });
      if (status) params.append('status', status);
      if (phase) params.append('phase', phase);
      if (searchTerm) params.append('search', searchTerm);

      const result = await apiRequest<Candidate[]>(`/candidates?${params}`);
      if (!result.success || !result.data) return;

      const rows = result.data.map(c => ({
        Nombre: c.first_name ? `${c.first_name} ${c.last_name || ''}`.trim() : c.whatsapp_name || 'Sin nombre',
        Telefono: c.whatsapp_phone,
        Fase: PHASE_NAMES[c.current_phase] || `Fase ${c.current_phase}`,
        Estado_Fase: c.phase_status,
        Estado: STATUS_LABELS[c.status] || c.status,
        Ultima_Actividad: new Date(c.last_interaction).toLocaleString('es-CO'),
        Registrado: new Date(c.created_at).toLocaleString('es-CO'),
      }));

      const headers = Object.keys(rows[0] || {});
      const csvContent = [
        headers.join(','),
        ...rows.map(row => headers.map(h => {
          const val = String((row as Record<string, string>)[h] || '');
          return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
        }).join(','))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidatos_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const getPhaseIndicator = (currentPhase: number, _phaseStatus: string) => (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((p) => (
          <div key={p} className={clsx('w-2 h-2 rounded-full', p < currentPhase ? 'bg-success-500' : p === currentPhase ? 'bg-primary-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600')} />
        ))}
      </div>
      <span className="text-sm text-gray-600 dark:text-gray-400">{PHASE_NAMES[currentPhase]}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Candidatos</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">{total} candidatos en total</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchCandidates} className="btn-secondary btn-sm"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={handleExport} disabled={isExporting} className="btn-secondary btn-sm">
            {isExporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            {isExporting ? 'Exportando...' : 'Exportar'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nombre o teléfono..." className="input pl-10" />
            </div>
          </form>
          <div className="flex gap-2 flex-wrap">
            <select value={status} onChange={(e) => setFilter('status', e.target.value)} className="input w-auto">
              <option value="">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="paused">Pausado</option>
              <option value="completed">Completado</option>
              <option value="abandoned">Abandonado</option>
            </select>
            <select value={phase} onChange={(e) => setFilter('phase', e.target.value)} className="input w-auto">
              <option value="">Todas las fases</option>
              {PHASE_NAMES.map((name, i) => (<option key={i} value={i}>Fase {i}: {name}</option>))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center"><div className="spinner mx-auto" /></div>
        ) : candidates.length === 0 ? (
          <div className="empty-state py-12">
            <Filter className="empty-state-icon" />
            <h3 className="empty-state-title">No hay candidatos</h3>
            <p className="empty-state-description">Ajusta los filtros o espera nuevos candidatos</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Candidato</th>
                  <th>Teléfono</th>
                  <th>Fase</th>
                  <th>Estado</th>
                  <th>Última actividad</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="avatar avatar-sm">{(c.first_name || c.whatsapp_name || '?').charAt(0).toUpperCase()}</div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{c.first_name ? `${c.first_name} ${c.last_name || ''}` : c.whatsapp_name || 'Sin nombre'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">ID: {c.id.slice(0, 12)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-gray-600 dark:text-gray-300">{c.whatsapp_phone}</td>
                    <td>{getPhaseIndicator(c.current_phase, c.phase_status)}</td>
                    <td>{getStatusBadge(c.status)}</td>
                    <td className="text-gray-500 dark:text-gray-400 text-sm">{new Date(c.last_interaction).toLocaleDateString('es-CO')}</td>
                    <td>
                      <Link to={`/candidates/${c.id}`} className="btn-ghost btn-sm"><Eye className="w-4 h-4" /></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-dark-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Página {page} de {totalPages}</p>
            <div className="flex gap-1">
              <button onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: String(page - 1) })} disabled={page <= 1} className="btn-ghost btn-sm disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: String(page + 1) })} disabled={page >= totalPages} className="btn-ghost btn-sm disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
